import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import { isNil } from 'lodash';

export type DropdownEntityCacheId = string | number;

export interface DropdownEntityCachePageInterface<T> {
  items: T[];
  count: number;
}

export interface DropdownEntityCacheByIdsResultInterface<T> {
  hits: T[];
  missing: DropdownEntityCacheId[];
}

/**
 * Session in-memory cache for dropdown multi/single-select options.
 * Survives component remounts (overflow / «Доп фильтры» toggle) so first-page
 * and hydrate requests are not repeated for the same kind + fingerprint / ids.
 */
@Injectable({ providedIn: 'root' })
export class DropdownEntityCacheService {
  private readonly byKind = new Map<string, Map<string, unknown>>();
  private readonly firstPages = new Map<string, DropdownEntityCachePageInterface<unknown>>();
  private readonly inflight = new Map<string, Observable<unknown>>();

  public fingerprint(source: Record<string, unknown>): string {
    return JSON.stringify(this.normalizeForFingerprint(source) ?? {});
  }

  public firstPageKey(kind: string, fingerprint: string): string {
    return `${kind}|${fingerprint}`;
  }

  public getFirstPage<T>(kind: string, fingerprint: string): DropdownEntityCachePageInterface<T> | null {
    const page = this.firstPages.get(this.firstPageKey(kind, fingerprint));
    if (!page) {
      return null;
    }

    return {
      items: [...(page.items as T[])],
      count: page.count
    };
  }

  public putFirstPage<T extends { id: DropdownEntityCacheId; }>(
    kind: string,
    fingerprint: string,
    page: DropdownEntityCachePageInterface<T>
  ): void {
    this.firstPages.set(this.firstPageKey(kind, fingerprint), {
      items: [...page.items],
      count: page.count
    });
    this.putEntities(kind, page.items);
  }

  public getByIds<T extends { id: DropdownEntityCacheId; }>(
    kind: string,
    ids: DropdownEntityCacheId[]
  ): DropdownEntityCacheByIdsResultInterface<T> {
    const store = this.byKind.get(kind);
    const hits: T[] = [];
    const missing: DropdownEntityCacheId[] = [];

    for (const id of ids) {
      const cached = store?.get(this.idKey(id)) as T | undefined;
      if (cached) {
        hits.push(cached);
      } else {
        missing.push(id);
      }
    }

    return { hits, missing };
  }

  public putEntities<T extends { id: DropdownEntityCacheId; }>(kind: string, items: T[]): void {
    if (!items?.length) {
      return;
    }

    let store = this.byKind.get(kind);
    if (!store) {
      store = new Map();
      this.byKind.set(kind, store);
    }

    for (const item of items) {
      if (isNil(item?.id)) {
        continue;
      }
      store.set(this.idKey(item.id), item);
    }
  }

  /**
   * Coalesce parallel identical fetches (e.g. two remounts at once).
   * Caller should put results into the cache on success when not using helpers below.
   */
  public runInflight<T>(key: string, factory: () => Observable<T>): Observable<T> {
    const existing = this.inflight.get(key);
    if (existing) {
      return existing as Observable<T>;
    }

    const shared = factory().pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => {
        this.inflight.delete(key);
      })
    );
    this.inflight.set(key, shared as Observable<unknown>);
    return shared;
  }

  /** Return cached first page or fetch once, then store. */
  public loadFirstPage<T extends { id: DropdownEntityCacheId; }>(
    kind: string,
    fingerprint: string,
    factory: () => Observable<DropdownEntityCachePageInterface<T>>
  ): Observable<DropdownEntityCachePageInterface<T>> {
    const cached = this.getFirstPage<T>(kind, fingerprint);
    if (cached) {
      return of(cached);
    }

    const key = `first:${this.firstPageKey(kind, fingerprint)}`;
    return this.runInflight(key, factory).pipe(
      tap((page) => {
        this.putFirstPage(kind, fingerprint, page);
      })
    );
  }

  /**
   * Resolve ids from byId cache; fetch only missing; return all requested entities
   * in the order of `ids` (hits + newly fetched).
   */
  public loadByIds<T extends { id: DropdownEntityCacheId; }>(
    kind: string,
    ids: DropdownEntityCacheId[],
    factory: (missing: DropdownEntityCacheId[]) => Observable<T[]>
  ): Observable<T[]> {
    const { hits, missing } = this.getByIds<T>(kind, ids);
    if (!missing.length) {
      return of(this.orderByIds(ids, hits));
    }

    const key = `hydrate:${kind}|${missing
      .map((id) => this.idKey(id))
      .sort()
      .join(',')}`;

    return this.runInflight(key, () => factory(missing)).pipe(
      tap((fetched) => {
        this.putEntities(kind, fetched);
      }),
      map((fetched) => this.orderByIds(ids, [...hits, ...fetched]))
    );
  }

  private orderByIds<T extends { id: DropdownEntityCacheId; }>(ids: DropdownEntityCacheId[], items: T[]): T[] {
    return ids
      .map((id) => items.find((item) => this.idKey(item.id) === this.idKey(id)))
      .filter(Boolean);
  }

  private idKey(id: DropdownEntityCacheId): string {
    return String(id);
  }

  private normalizeForFingerprint(value: unknown): unknown {
    if (isNil(value) || value === '') {
      return undefined;
    }

    if (Array.isArray(value)) {
      const normalized = value
        .map((item) => this.normalizeForFingerprint(item))
        .filter((item) => item !== undefined);
      return normalized.length ? normalized : undefined;
    }

    if (typeof value === 'object') {
      const record = value as Record<string, unknown>;
      const keys = Object.keys(record).sort();
      const out: Record<string, unknown> = {};
      for (const key of keys) {
        if (key === 'offset' || key === 'search') {
          continue;
        }
        const normalized = this.normalizeForFingerprint(record[key]);
        if (normalized !== undefined) {
          out[key] = normalized;
        }
      }
      return Object.keys(out).length ? out : undefined;
    }

    return value;
  }
}
